import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUpIcon, StarIcon, UserIcon, MessageSquareIcon, CalendarIcon, CheckIcon, XIcon, BookmarkIcon, EyeIcon, BellIcon, TrophyIcon, UsersIcon, BarChartIcon, HeartIcon, ClockIcon, DownloadIcon } from 'lucide-react';
import { ChevronRightIcon } from 'lucide-react';
const MentorDashboard: React.FC = () => {
  const [showResourcePreview, setShowResourcePreview] = useState<number | null>(null);
  const [showTrophyDetails, setShowTrophyDetails] = useState<number | null>(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showMenteeDetailModal, setShowMenteeDetailModal] = useState<{
    id: number;
    name: string;
    image: string;
    progress: number;
    nextSession: string;
    goals: string[];
  } | null>(null);
  const navigate = useNavigate();
  const dashboardData = {
    mentor: {
      name: 'Alex',
      image: 'https://randomuser.me/api/portraits/women/44.jpg',
      role: 'Senior UX Designer',
      experience: '8 years',
      impactMetrics: {
        totalMentees: 12,
        activeEngagements: 3,
        sessionHours: 48,
        completedGoals: 15
      }
    },
    activeMentees: [{
      id: 1,
      name: 'Jordan Lee',
      image: 'https://randomuser.me/api/portraits/women/33.jpg',
      progress: 65,
      nextSession: 'Tomorrow at 3:00 PM',
      goals: ['Complete UX portfolio', 'Improve user research skills', 'Learn design systems']
    }, {
      id: 2,
      name: 'Sam Chen',
      image: 'https://randomuser.me/api/portraits/men/32.jpg',
      progress: 40,
      nextSession: 'Thursday at 2:00 PM',
      goals: ['Master Figma advanced features', 'Develop leadership skills', 'Learn accessibility practices']
    }],
    recommendedResources: [{
      id: 1,
      title: 'Guide: Effective Design Feedback',
      description: 'Learn how to provide constructive feedback that helps mentees grow.',
      type: 'Article'
    }, {
      id: 2,
      title: 'Mentorship Best Practices',
      description: 'Tips and strategies for successful mentoring relationships.',
      type: 'Guide'
    }],
    upcomingSessions: [{
      id: 1,
      menteeName: 'Jordan Lee',
      date: 'Tomorrow',
      time: '3:00 PM EST',
      type: 'Portfolio Review'
    }, {
      id: 2,
      menteeName: 'Sam Chen',
      date: 'Thursday',
      time: '2:00 PM EST',
      type: 'Goal Setting'
    }],
    trophies: [{
      id: 1,
      emoji: '🌟',
      title: 'Mentor Excellence',
      description: 'Successfully completed 10 mentorship sessions',
      points: 100,
      date: 'Feb 15, 2024',
      visible: true
    }, {
      id: 2,
      emoji: '🎯',
      title: 'Goal Champion',
      description: 'Helped 5 mentees achieve their goals',
      points: 75,
      date: 'Feb 10, 2024',
      visible: true
    }]
  };
  const quotes = [{
    text: "Openness may not completely disarm prejudice, but it's a good place to start.",
    author: 'Jason Collins'
  }, {
    text: 'Hope will never be silent.',
    author: 'Harvey Milk'
  }, {
    text: 'Your silence will not protect you.',
    author: 'Audre Lorde'
  }, {
    text: "There's nothing wrong with you. There's a lot wrong with the world you live in.",
    author: 'Chris Colfer'
  }, {
    text: 'I am deliberate and afraid of nothing.',
    author: 'Audre Lorde'
  }, {
    text: 'Be who you needed when you were younger.',
    author: 'Laverne Cox'
  }, {
    text: 'I believe that telling our stories, first to ourselves and then to one another, and the world, is a revolutionary act.',
    author: 'Janet Mock'
  }];
  const getDayOfYear = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  };
  const todayQuote = quotes[getDayOfYear() % quotes.length];
  const handleDownloadImpact = () => {
    // In a real implementation, this would generate a PDF with the mentor's impact metrics
    const metrics = {
      menteesHelped: dashboardData.mentor.impactMetrics.totalMentees,
      activeEngagements: dashboardData.mentor.impactMetrics.activeEngagements,
      sessionHours: dashboardData.mentor.impactMetrics.sessionHours,
      completedGoals: dashboardData.mentor.impactMetrics.completedGoals
    };
    // For now, just download the metrics as a JSON file
    const element = document.createElement('a');
    const file = new Blob([JSON.stringify(metrics, null, 2)], {
      type: 'application/json'
    });
    element.href = URL.createObjectURL(file);
    element.download = 'mentor-impact-metrics.json';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };
  const ResourcePreviewModal = ({
    resource
  }: {
    resource: any;
  }) => {
    if (!resource) return null;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-992">
        <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto" data-id="element-993">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-994">
            <h3 className="font-semibold" data-id="element-995">Resource Preview</h3>
            <button onClick={() => setShowResourcePreview(null)} data-id="element-996">
              <XIcon size={20} data-id="element-997" />
            </button>
          </div>
          <div className="p-4" data-id="element-998">
            <h2 className="text-lg font-semibold mb-2" data-id="element-999">{resource.title}</h2>
            <p className="text-gray-600 mb-4" data-id="element-1000">{resource.description}</p>
            <div className="flex items-center justify-between" data-id="element-1001">
              <span className="text-sm bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full" data-id="element-1002">
                {resource.type}
              </span>
              <button className="text-purple-600 hover:text-purple-700" data-id="element-1003">
                <BookmarkIcon size={16} data-id="element-1004" />
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  const MenteeDetailModal = ({
    mentee
  }: {
    mentee: any;
  }) => {
    if (!mentee) return null;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1005">
        <div className="bg-white rounded-xl w-full max-w-md" data-id="element-1006">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1007">
            <div className="flex items-center" data-id="element-1008">
              <img src={mentee.image} alt={mentee.name} className="w-10 h-10 rounded-full mr-3" data-id="element-1009" />
              <div data-id="element-1010">
                <h3 className="font-semibold" data-id="element-1011">{mentee.name}</h3>
                <span className="text-sm text-gray-500" data-id="element-1012">
                  Next session: {mentee.nextSession}
                </span>
              </div>
            </div>
            <button onClick={() => setShowMenteeDetailModal(null)} data-id="element-1013">
              <XIcon size={20} data-id="element-1014" />
            </button>
          </div>
          <div className="p-4" data-id="element-1015">
            <div className="mb-4" data-id="element-1016">
              <h4 className="font-medium mb-2" data-id="element-1017">Progress</h4>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-1" data-id="element-1018">
                <div className="bg-purple-600 h-2 rounded-full" style={{
                width: `${mentee.progress}%`
              }} data-id="element-1019" />
              </div>
              <span className="text-sm text-gray-600" data-id="element-1020">
                {mentee.progress}% complete
              </span>
            </div>
            <div className="mb-4" data-id="element-1021">
              <h4 className="font-medium mb-2" data-id="element-1022">Active Goals</h4>
              <div className="space-y-2" data-id="element-1023">
                {mentee.goals.map((goal: string, index: number) => <div key={index} className="flex items-start" data-id="element-1024">
                    <CheckIcon size={16} className="text-purple-600 mr-2 mt-0.5" data-id="element-1025" />
                    <span className="text-sm text-gray-600" data-id="element-1026">{goal}</span>
                  </div>)}
              </div>
            </div>
            <div className="flex justify-end space-x-3" data-id="element-1027">
              <button onClick={() => navigate('/messages')} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-1028">
                <MessageSquareIcon size={16} className="mr-2" data-id="element-1029" />
                Message
              </button>
              <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" onClick={() => setShowMenteeDetailModal(null)} data-id="element-1030">
                View Full Profile
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="space-y-6 pb-4" data-id="element-1031">
      <div className="bg-white -mx-4 px-4 pt-6 pb-6 mb-6" data-id="element-1032">
        <div className="text-center mb-6" data-id="element-1033">
          <h1 className="text-2xl font-bold mb-2" data-id="element-1034">
            Welcome back, {dashboardData.mentor.name}!
          </h1>
          <div className="w-full overflow-hidden" data-id="element-1035">
            <div className="animate-scroll-left hover:pause" data-id="element-1036">
              <span className="inline-block text-gray-600 italic" data-id="element-1037">
                "{todayQuote.text}" – {todayQuote.author}
              </span>
            </div>
          </div>
        </div>
        {/* Stats Carousel - Updated Design */}
        <div className="relative" data-id="element-1038">
          <div className="overflow-x-auto snap-x snap-mandatory hide-scrollbar" data-id="element-1039">
            <div className="flex gap-4 pb-4 px-2" data-id="element-1040">
              {/* Active Mentees */}
              <div className="flex-shrink-0 w-[120px] h-[120px] bg-purple-50 rounded-xl p-4 snap-center shadow-sm flex flex-col items-center" data-id="element-1041">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-1042">
                  <HeartIcon className="w-6 h-6 text-[#8F3FFF] stroke-[2.5]" data-id="element-1043" />
                  <span className="text-2xl font-bold text-[#8F3FFF]" data-id="element-1044">
                    {dashboardData.mentor.impactMetrics.activeEngagements}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 text-center" data-id="element-1045">
                  Active Mentees
                </span>
              </div>
              {/* Total Mentees */}
              <div className="flex-shrink-0 w-[120px] h-[120px] bg-purple-50 rounded-xl p-4 snap-center shadow-sm flex flex-col items-center" data-id="element-1046">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-1047">
                  <UsersIcon className="w-6 h-6 text-[#8F3FFF] stroke-[2.5]" data-id="element-1048" />
                  <span className="text-2xl font-bold text-[#8F3FFF]" data-id="element-1049">
                    {dashboardData.mentor.impactMetrics.totalMentees}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 text-center" data-id="element-1050">
                  Mentees Helped
                </span>
              </div>
              {/* Session Hours */}
              <div className="flex-shrink-0 w-[120px] h-[120px] bg-purple-50 rounded-xl p-4 snap-center shadow-sm flex flex-col items-center" data-id="element-1051">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-1052">
                  <ClockIcon className="w-6 h-6 text-[#8F3FFF] stroke-[2.5]" data-id="element-1053" />
                  <span className="text-2xl font-bold text-[#8F3FFF]" data-id="element-1054">
                    {dashboardData.mentor.impactMetrics.sessionHours}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 text-center" data-id="element-1055">
                  Session Hours
                </span>
              </div>
              {/* Goals Completed - Adjusted alignment */}
              <div className="flex-shrink-0 w-[120px] h-[120px] bg-purple-50 rounded-xl p-4 snap-center shadow-sm flex flex-col items-center" data-id="element-1056">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-1057">
                  <CheckIcon className="w-6 h-6 text-[#8F3FFF] stroke-[2.5]" data-id="element-1058" />
                  <span className="text-2xl font-bold text-[#8F3FFF]" data-id="element-1059">
                    {dashboardData.mentor.impactMetrics.completedGoals}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 text-center" data-id="element-1060">
                  Goals Done
                </span>
              </div>
              {/* Download Impact */}
              <button onClick={handleDownloadImpact} className="flex-shrink-0 w-[120px] h-[120px] bg-purple-50 rounded-xl p-4 snap-center shadow-sm flex flex-col items-center hover:bg-purple-100 transition-colors" data-id="element-1061">
                <div className="flex items-center justify-center mb-3" data-id="element-1062">
                  <DownloadIcon className="w-6 h-6 text-[#8F3FFF] stroke-[2.5]" data-id="element-1063" />
                </div>
                <span className="text-sm font-medium text-gray-900 text-center" data-id="element-1064">
                  Download Impact
                </span>
              </button>
            </div>
          </div>
          {/* Scroll Indicator */}
          <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 bg-gradient-to-l from-white via-white/80 to-transparent pl-8 pr-2 py-2" data-id="element-1065">
            <ChevronRightIcon className="w-5 h-5 text-gray-400 animate-pulse" data-id="element-1066" />
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6" data-id="element-1067">
        <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-1068">
          <h2 className="text-lg font-semibold mb-4" data-id="element-1069">Active Mentees</h2>
          <div className="space-y-4" data-id="element-1070">
            {dashboardData.activeMentees.map(mentee => <div key={mentee.id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:border-purple-200 transition-colors cursor-pointer" onClick={() => setShowMenteeDetailModal(mentee)} data-id="element-1071">
                <div className="flex items-center" data-id="element-1072">
                  <img src={mentee.image} alt={mentee.name} className="w-10 h-10 rounded-full object-cover" data-id="element-1073" />
                  <div className="ml-3" data-id="element-1074">
                    <h3 className="font-medium" data-id="element-1075">{mentee.name}</h3>
                    <p className="text-sm text-gray-500" data-id="element-1076">
                      Next: {mentee.nextSession}
                    </p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-1077">
                  <span className="text-sm text-purple-600 font-medium mr-2" data-id="element-1078">
                    {mentee.progress}%
                  </span>
                  <div size={16} className="text-gray-400" data-id="element-1079" />
                </div>
              </div>)}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-1080">
          <h2 className="text-lg font-semibold mb-4" data-id="element-1081">Upcoming Sessions</h2>
          <div className="space-y-4" data-id="element-1082">
            {dashboardData.upcomingSessions.map(session => <div key={session.id} className="p-4 border border-gray-200 rounded-lg" data-id="element-1083">
                <div className="flex justify-between items-start mb-2" data-id="element-1084">
                  <div data-id="element-1085">
                    <h3 className="font-medium" data-id="element-1086">{session.menteeName}</h3>
                    <p className="text-sm text-gray-500" data-id="element-1087">{session.type}</p>
                  </div>
                  <div className="text-right" data-id="element-1088">
                    <p className="font-medium" data-id="element-1089">{session.date}</p>
                    <p className="text-sm text-gray-500" data-id="element-1090">{session.time}</p>
                  </div>
                </div>
                <div className="flex justify-end space-x-2" data-id="element-1091">
                  <button className="text-sm text-purple-600 hover:text-purple-700" data-id="element-1092">
                    Reschedule
                  </button>
                  <button className="text-sm bg-purple-600 text-white px-3 py-1 rounded-lg hover:bg-purple-700" data-id="element-1093">
                    Join
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-1094">
        <h2 className="text-lg font-semibold mb-4" data-id="element-1095">Recommended Resources</h2>
        <div className="grid md:grid-cols-2 gap-4" data-id="element-1096">
          {dashboardData.recommendedResources.map(resource => <div key={resource.id} className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-purple-200" onClick={() => setShowResourcePreview(resource.id)} data-id="element-1097">
              <div className="flex items-start justify-between" data-id="element-1098">
                <div data-id="element-1099">
                  <h3 className="font-medium mb-1" data-id="element-1100">{resource.title}</h3>
                  <p className="text-sm text-gray-600 mb-2" data-id="element-1101">
                    {resource.description}
                  </p>
                  <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full" data-id="element-1102">
                    {resource.type}
                  </span>
                </div>
                <BookmarkIcon size={16} className="text-gray-400" data-id="element-1103" />
              </div>
            </div>)}
        </div>
      </div>
      {showResourcePreview !== null && <ResourcePreviewModal resource={dashboardData.recommendedResources.find(r => r.id === showResourcePreview)} data-id="element-1104" />}
      {showMenteeDetailModal && <MenteeDetailModal mentee={showMenteeDetailModal} data-id="element-1105" />}
    </div>;
};
export default MentorDashboard;
// Add these styles to your CSS
const styles = `
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
`;